import { useState, useEffect, useRef, useCallback } from 'react';
import { uuid } from '@/lib/utils';

/**
 * Hook for leader election between browser tabs using BroadcastChannel.
 * Only the leader tab will process notifications (ring sounds, etc.)
 * to avoid duplicate sounds across multiple tabs.
 */
export function useTabLeader(channelName: string = 'video-ring-leader') {
  const [isLeader, setIsLeader] = useState(false);
  const tabIdRef = useRef<string>(uuid());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastLeaderHeartbeatRef = useRef<number>(Date.now());
  const currentLeaderRef = useRef<string | null>(null);

  const HEARTBEAT_INTERVAL_MS = 2000; // 2 seconds
  const LEADER_TIMEOUT_MS = 5000; // 5 seconds without heartbeat = assume leader is gone

  const claimLeadership = useCallback(() => {
    console.log(`[TabLeader] 👑 Tab ${tabIdRef.current.slice(0, 8)} claiming leadership`);
    setIsLeader(true);
    currentLeaderRef.current = tabIdRef.current;
    
    // Announce leadership to other tabs
    channelRef.current?.postMessage({
      type: 'leader-claim',
      tabId: tabIdRef.current,
      timestamp: Date.now(),
    });
  }, []);

  const sendHeartbeat = useCallback(() => {
    if (currentLeaderRef.current === tabIdRef.current) {
      channelRef.current?.postMessage({
        type: 'heartbeat',
        tabId: tabIdRef.current,
        timestamp: Date.now(),
      });
    }
  }, []);

  useEffect(() => {
    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel === 'undefined') {
      console.log('[TabLeader] BroadcastChannel not supported, becoming default leader');
      setIsLeader(true);
      return;
    }

    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;

    // Handle messages from other tabs
    channel.onmessage = (event) => {
      const { type, tabId, timestamp } = event.data;

      if (type === 'leader-claim') {
        console.log(`[TabLeader] Tab ${tabId.slice(0, 8)} claimed leadership`);
        currentLeaderRef.current = tabId;
        lastLeaderHeartbeatRef.current = timestamp;
        
        // If another tab claimed leadership, we are not the leader
        if (tabId !== tabIdRef.current) {
          setIsLeader(false);
        }
      } else if (type === 'heartbeat') {
        if (tabId === currentLeaderRef.current) {
          lastLeaderHeartbeatRef.current = timestamp;
        }
      } else if (type === 'request-leader') {
        // Another tab is asking who the leader is
        if (currentLeaderRef.current === tabIdRef.current) {
          // I'm the leader, announce myself
          channel.postMessage({
            type: 'leader-claim',
            tabId: tabIdRef.current,
            timestamp: Date.now(),
          });
        }
      }
    };

    // Request current leader on startup
    channel.postMessage({
      type: 'request-leader',
      tabId: tabIdRef.current,
      timestamp: Date.now(),
    });

    // Wait a bit for response, then claim leadership if no one responds
    const initialTimeout = setTimeout(() => {
      const timeSinceLastHeartbeat = Date.now() - lastLeaderHeartbeatRef.current;
      if (!currentLeaderRef.current || timeSinceLastHeartbeat > LEADER_TIMEOUT_MS) {
        claimLeadership();
      }
    }, 500); // Wait 500ms for existing leader to respond

    // Setup heartbeat interval
    heartbeatIntervalRef.current = setInterval(() => {
      // Send heartbeat if we're the leader
      sendHeartbeat();

      // Check if current leader has timed out
      const timeSinceLastHeartbeat = Date.now() - lastLeaderHeartbeatRef.current;
      if (currentLeaderRef.current !== tabIdRef.current && timeSinceLastHeartbeat > LEADER_TIMEOUT_MS) {
        console.log('[TabLeader] Leader timed out, claiming leadership');
        claimLeadership();
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimeout);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      channel.close();
    };
  }, [channelName, claimLeadership, sendHeartbeat]);

  return {
    isLeader,
    tabId: tabIdRef.current,
  };
}
